import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import MaintenancePage from "@/components/MaintenancePage";
import { api } from "@/lib/api";
import ErrorBoundary from "@/components/ErrorBoundary";

// Public
import Home from "@/pages/public/Home";
import About from "@/pages/public/About";
import Gallery from "@/pages/public/Gallery";
import Contact from "@/pages/public/Contact";
import Donate from "@/pages/public/Donate";
import InternshipsPublic from "@/pages/public/InternshipsPublic";
import TestSeriesPublic from "@/pages/public/TestSeriesPublic";
import Programs from "@/pages/public/Programs";
import PrivacyPolicy from "@/pages/public/PrivacyPolicy";
import Terms from "@/pages/public/Terms";
import RefundPolicy from "@/pages/public/RefundPolicy";
import DonationResult from "@/pages/DonationResult";

// Auth
import AdminLogin from "@/pages/auth/AdminLogin";
import Login from "@/pages/auth/Login";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import Register from "@/pages/auth/Register";
import VerifyCertificate from "@/pages/public/VerifyCertificate";

// Student
import StudentDashboard from "@/pages/student/StudentDashboard";
import StudentInternships from "@/pages/student/StudentInternships";
import StudentApplications from "@/pages/student/StudentApplications";
import StudentTestSeries from "@/pages/student/StudentTestSeries";
import StudentResults from "@/pages/student/StudentResults";
import StudentProfile from "@/pages/student/StudentProfile";
import StudentSocialWork from "@/pages/student/StudentSocialWork";
import StudentCourses from "@/pages/student/StudentCourses";
import StudentCoursePlayer from "@/pages/student/StudentCoursePlayer";
import Exam from "@/pages/student/Exam";

// Admin
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminVolunteers from "@/pages/admin/AdminVolunteers";
import AdminInternships from "@/pages/admin/AdminInternships";
import AdminApplications from "@/pages/admin/AdminApplications";
import AdminTestSeries from "@/pages/admin/AdminTestSeries";
import AdminStudentDetail from "@/pages/admin/AdminStudentDetail";
import AdminReferralTree from "@/pages/admin/AdminReferralTree";
import AdminAttempts from "@/pages/admin/AdminAttempts";
import AdminLiveProctor from "@/pages/admin/AdminLiveProctor";
import AdminCheatingLogs from "@/pages/admin/AdminCheatingLogs";
import AdminPayments from "@/pages/admin/AdminPayments";
import AdminDonations from "@/pages/admin/AdminDonations";
import AdminGallery from "@/pages/admin/AdminGallery";
import AdminHomepage from "@/pages/admin/AdminHomepage";

import AdminAmbassadors from "@/pages/admin/AdminAmbassadors";
import AdminSocialWork from "@/pages/admin/AdminSocialWork";
import AdminSocialWorkApplications from "@/pages/admin/AdminSocialWorkApplications";
import AdminCourses from "@/pages/admin/AdminCourses";

// Volunteer
import VolunteerDashboard from "@/pages/volunteer/VolunteerDashboard";

// Ambassador
import AmbassadorDashboard from "@/pages/ambassador/AmbassadorDashboard";

// Payment
import PaymentResult from "@/pages/PaymentResult";

function App() {
  const [maint, setMaint] = useState({ enabled: false, message: "", contact: "" });
  const [maintLoaded, setMaintLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/public/maintenance")
      .then((r) => { if (alive) setMaint(r.data || {}); })
      .catch(() => {})
      .finally(() => { if (alive) setMaintLoaded(true); });
    // Re-poll every 60s so admins toggling it off is reflected without reload
    const id = setInterval(() => {
      api.get("/public/maintenance").then((r) => alive && setMaint(r.data || {})).catch(() => {});
    }, 60000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  // Admin login URL + verify URL are always reachable so admins / verifiers
  // can disable maintenance / verify a certificate even during maintenance.
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  const isAdminBypassPath =
    path.startsWith("/pmrwelfarefoundation.org-admin") ||
    path.startsWith("/admin") ||
    path.startsWith("/verify") ||
    path.startsWith("/api/files/");

  if (maintLoaded && maint.enabled && !isAdminBypassPath) {
    return <MaintenancePage message={maint.message} contact={maint.contact} />;
  }

  return (
    <AuthProvider>
      <BrowserRouter>
        <ErrorBoundary>
          <Routes>
          {/* Public */}
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/donate" element={<Donate />} />
          <Route path="/internships" element={<InternshipsPublic />} />
          <Route path="/test-series" element={<TestSeriesPublic />} />
          <Route path="/programs" element={<Programs />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/refund" element={<RefundPolicy />} />
          <Route path="/verify" element={<VerifyCertificate />} />
          <Route path="/donation/success" element={<DonationResult success={true} />} />
          <Route path="/donation/failure" element={<DonationResult success={false} />} />

          {/* Auth */}
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/register" element={<Register />} />
          {/* Secret admin portal (keep this URL private) */}
          <Route path="/pmrwelfarefoundation.org-admin" element={<AdminLogin />} />

          {/* Student */}
          <Route path="/student" element={<ProtectedRoute roles={["student"]}><StudentDashboard /></ProtectedRoute>} />
          <Route path="/student/internships" element={<ProtectedRoute roles={["student"]}><StudentInternships /></ProtectedRoute>} />
          <Route path="/student/applications" element={<ProtectedRoute roles={["student"]}><StudentApplications /></ProtectedRoute>} />
          <Route path="/student/test-series" element={<ProtectedRoute roles={["student"]}><StudentTestSeries /></ProtectedRoute>} />
          <Route path="/student/results" element={<ProtectedRoute roles={["student"]}><StudentResults /></ProtectedRoute>} />
          <Route path="/student/profile" element={<ProtectedRoute roles={["student"]}><StudentProfile /></ProtectedRoute>} />
          <Route path="/student/social-work" element={<ProtectedRoute roles={["student"]}><StudentSocialWork /></ProtectedRoute>} />
          <Route path="/student/courses" element={<ProtectedRoute roles={["student"]}><StudentCourses /></ProtectedRoute>} />
          <Route path="/student/courses/:course_id" element={<ProtectedRoute roles={["student"]}><StudentCoursePlayer /></ProtectedRoute>} />
          <Route path="/exam/:testId" element={<ProtectedRoute roles={["student"]}><Exam /></ProtectedRoute>} />

          {/* Payment */}
          <Route path="/payment/success" element={<PaymentResult success={true} />} />
          <Route path="/payment/failure" element={<PaymentResult success={false} />} />

          {/* Admin */}
          <Route path="/admin" element={<ProtectedRoute roles={["admin"]}><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admin/students" element={<ProtectedRoute roles={["admin"]}><AdminUsers role="student" title="Students" /></ProtectedRoute>} />
          <Route path="/admin/students/:user_id" element={<ProtectedRoute roles={["admin"]}><AdminStudentDetail /></ProtectedRoute>} />
          <Route path="/admin/volunteers" element={<ProtectedRoute roles={["admin"]}><AdminVolunteers /></ProtectedRoute>} />
          <Route path="/admin/internships" element={<ProtectedRoute roles={["admin", "volunteer"]}><AdminInternships /></ProtectedRoute>} />
          <Route path="/admin/applications" element={<ProtectedRoute roles={["admin", "volunteer"]}><AdminApplications /></ProtectedRoute>} />
          <Route path="/admin/test-series" element={<ProtectedRoute roles={["admin", "volunteer"]}><AdminTestSeries /></ProtectedRoute>} />
          <Route path="/admin/attempts" element={<ProtectedRoute roles={["admin", "volunteer"]}><AdminAttempts /></ProtectedRoute>} />
          <Route path="/admin/live-proctor" element={<ProtectedRoute roles={["admin", "volunteer"]}><AdminLiveProctor /></ProtectedRoute>} />
          <Route path="/admin/cheating-logs" element={<ProtectedRoute roles={["admin", "volunteer"]}><AdminCheatingLogs /></ProtectedRoute>} />
          <Route path="/admin/referral-tree" element={<ProtectedRoute roles={["admin"]}><AdminReferralTree /></ProtectedRoute>} />
          <Route path="/admin/payments" element={<ProtectedRoute roles={["admin", "volunteer"]}><AdminPayments /></ProtectedRoute>} />
          <Route path="/admin/donations" element={<ProtectedRoute roles={["admin", "volunteer"]}><AdminDonations /></ProtectedRoute>} />
          <Route path="/admin/gallery" element={<ProtectedRoute roles={["admin", "volunteer"]}><AdminGallery /></ProtectedRoute>} />
          <Route path="/admin/homepage" element={<ProtectedRoute roles={["admin"]}><AdminHomepage /></ProtectedRoute>} />
          <Route path="/admin/ambassadors" element={<ProtectedRoute roles={["admin"]}><AdminAmbassadors /></ProtectedRoute>} />
          <Route path="/admin/social-work" element={<ProtectedRoute roles={["admin", "volunteer"]}><AdminSocialWork /></ProtectedRoute>} />
          <Route path="/admin/social-work-applications" element={<ProtectedRoute roles={["admin", "volunteer"]}><AdminSocialWorkApplications /></ProtectedRoute>} />
          <Route path="/admin/courses" element={<ProtectedRoute roles={["admin", "volunteer"]}><AdminCourses /></ProtectedRoute>} />

          {/* Volunteer */}
          <Route path="/volunteer" element={<ProtectedRoute roles={["volunteer"]}><VolunteerDashboard /></ProtectedRoute>} />

          {/* Ambassador */}
          <Route path="/ambassador" element={<ProtectedRoute roles={["ambassador"]}><AmbassadorDashboard /></ProtectedRoute>} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </ErrorBoundary>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
